import useAppStore from '@/h5/stores/app'
import { cloneDeep, isEqual } from 'lodash'
import { urlParam2Obj, getLanguage } from '@/packages/plugins/utils'
import { compareWithCurrentUTC } from '@/packages/plugins/dateUtils'

// 立即执行函数的UUID配置
const immediateFuncUUID = [import.meta.env.VITE_APP_H5_DATALANG || '', import.meta.env.VITE_APP_H5_DATALANG_OLD || '']

const isObject = (value) => {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/**
 * 数据源/脚本对「是否隐藏」的产出归一化为 boolean，减少与当前意图一致时的无意义 ref 写入（减轻显隐 watch、flush）
 * @returns {boolean} 是否执行了写入
 */
export const setDataSourceHidenIfChanged = (hidenRef, raw) => {
  const next = raw === true || raw === 1 || raw === '1' || raw === 'true' ? true : raw === false || raw === 0 || raw === '0' || raw === 'false' ? false : !!raw
  if (hidenRef.value === next) {
    return false
  }
  hidenRef.value = next
  return true
}

const DATA_SOURCE_DERIVED_UNSET = Symbol('dataSourceDerivedUnset')

/** 比较数据源派生值（组件实际消费的切片/脚本结果） */
export const isDerivedValueEqual = (prev, next) => {
  if (prev === next) return true
  if (prev === DATA_SOURCE_DERIVED_UNSET) return false
  if (prev === null || prev === undefined || next === null || next === undefined) {
    return prev === next
  }
  const typePrev = typeof prev
  const typeNext = typeof next
  if (typePrev !== typeNext) return false
  if (typePrev !== 'object') return prev === next
  return isEqual(prev, next)
}

/** 每个 cb 缓存上次派生值，相同则跳过后续赋值 */
const createDerivedValueCache = () => {
  let last = DATA_SOURCE_DERIVED_UNSET
  return (next) => {
    if (isDerivedValueEqual(last, next)) {
      return true
    }
    last = next
    return false
  }
}

/** 派生值变化时才写入 nodeData，避免无效 Vue 更新 */
export const setNodeDataFieldIfChanged = (nodeData, fieldKey, next) => {
  if (isDerivedValueEqual(nodeData.value[fieldKey], next)) {
    return false
  }
  nodeData.value[fieldKey] = next
  return true
}

/** 是否为依赖统一列表数据源的填充绑定（keys 含当前列表项 [] 段） */
export const isUnifiedListDataSourceBinding = (source, listIndex) => {
  if (!listIndex?.source?.uuid || !source?.uuid || !source?.keys) return false
  if (source.uuid !== listIndex.source.uuid) return false
  if (source.uuid.includes('xy-list-item-layout')) return false
  const listKeys = listIndex.source.keys
  if (!listKeys || !source.keys.includes('[]')) return false
  const arrayPrefix = listKeys.endsWith('[]') ? listKeys : `${listKeys}[]`
  return source.keys === arrayPrefix || source.keys.startsWith(`${arrayPrefix}.`) || source.keys.startsWith(`${arrayPrefix}[`)
}

/** 脚本依赖的数据源是否全部来自统一列表项 */
export const isAllUnifiedListDataSourceBindings = (sources, listIndex) => {
  if (!sources?.length || !listIndex) return false
  return sources.every((source) => isUnifiedListDataSourceBinding(source, listIndex))
}

/** 从 listItemData 收集脚本入参 */
const collectListItemScriptFuncData = (sources, listIndex, listItemData) => {
  const funcData = {}
  let isAllDataReady = true
  sources.forEach((source, index) => {
    const sd = getListItemFillValue(listItemData, source, listIndex)
    funcData[index + ''] = sd
    if (sd === undefined || sd === null) {
      isAllDataReady = false
    }
  })
  return { funcData, isAllDataReady }
}

/** 基于 listItemData 执行脚本并写入组件数据 */
const applyListItemScriptDataSource = (config, fieldKey, nodeData, hidenEl, listIndex, listItemData, shouldSkipDerived) => {
  let compiledFunc = null
  try {
    compiledFunc = convertStringToFunc(config.script)
  } catch (error) {
    console.error('数据源脚本编译错误', fieldKey, error)
    return
  }
  const { funcData, isAllDataReady } = collectListItemScriptFuncData(config.source, listIndex, listItemData)
  if (!isAllDataReady || !compiledFunc) return
  if (shouldSkipDerived(funcData)) return
  try {
    const nextValue = compiledFunc(...Object.values(funcData))
    if (fieldKey === 'hiden') {
      setDataSourceHidenIfChanged(hidenEl, nextValue)
    } else {
      setNodeDataFieldIfChanged(nodeData, fieldKey, nextValue)
    }
  } catch (error) {
    console.error('数据源脚本执行错误', fieldKey, error)
  }
}

/** 从传入的列表项数据中读取填充字段值 */
export const getListItemFillValue = (listItemData, source, listIndex) => {
  if (listItemData === undefined || listItemData === null) return undefined
  const listKeys = listIndex.source.keys
  const arrayPrefix = listKeys.endsWith('[]') ? listKeys : `${listKeys}[]`
  if (source.keys === arrayPrefix) {
    return listItemData
  }
  if (!source.keys.startsWith(`${arrayPrefix}.`) && !source.keys.startsWith(`${arrayPrefix}[`)) {
    return undefined
  }
  const relativePath = source.keys.slice(arrayPrefix.length + 1)
  return getSourceMapData(listItemData, { ...source, keys: relativePath }, '')
}

/** 列表项数据变更时，批量应用统一列表数据源的填充/脚本绑定 */
export const applyListItemDataSourceFills = (dataSource, nodeData, hidenEl, listIndex, listItemData) => {
  if (!dataSource || !listIndex || listItemData === undefined) return
  Object.keys(dataSource).forEach((key) => {
    const config = dataSource[key]
    const shouldSkipDerived = createDerivedValueCache()
    if (config.type === 'script') {
      if (isAllUnifiedListDataSourceBindings(config.source, listIndex)) {
        applyListItemScriptDataSource(config, key, nodeData, hidenEl, listIndex, listItemData, shouldSkipDerived)
      }
      return
    }
    const source = config.source?.[0]
    if (!source || !isUnifiedListDataSourceBinding(source, listIndex)) return
    const value = getListItemFillValue(listItemData, source, listIndex)
    if (value !== undefined && value !== null) {
      if (key === 'hiden') {
        setDataSourceHidenIfChanged(hidenEl, value)
      } else {
        setNodeDataFieldIfChanged(nodeData, key, value)
      }
    }
  })
}

export const convertStringToFunc = (arrowFuncStr) => {
  // 去掉前后空格
  arrowFuncStr = arrowFuncStr.trim()
  // 使用正则表达式匹配箭头函数的参数和函数体
  const match = arrowFuncStr.match(/^\s*\(([^)]*)\)\s*=>\s*{([\s\S]*)}$/)
  if (!match) {
    throw new Error('Invalid arrow function string')
  }

  // 获取箭头函数的参数和函数体
  const params = match[1].split(',').map((param) => param.trim())
  const body = match[2].trim()

  // 创建一个新的函数
  const func = new Function(...params, body)

  return func
}

const containsArrayIndex = (str) => {
  // 正则表达式匹配 "[任意数字]"
  const pattern = /\[\d+\]/
  return pattern.test(str)
}

const extractAndRemoveArrayIndex = (str) => {
  // 提取[]中的内容
  const match = str.match(/\[(.*?)\]/)
  const index = match ? match[1] : null

  // 去除[0]
  const map = str.replace(/\[\d+\]/, '')

  return {
    index,
    map
  }
}

export const getSourceMapData = (data, source, subIndexStr) => {
  // 将索引字符串和下标字符串分割成数组
  const indexStr = source.keys
  if (!indexStr) {
    return null
  }
  // console.log('getSourceMapData', source)
  if (immediateFuncUUID.includes(source.uuid) || source.uuid === 'data-source_timeInfo' || source.uuid === 'data-source_addClickCustomParams') {
    // 判断是否为自定义方法数据
    return getFuncData(source)
  } else if (source.uuid.includes('xy-list-item-layout') && source.keys === 'index') {
    // 获取列表项索引
    return subIndexStr
  } else if (source.uuid.includes('xy-list-item-layout') && source.keys === 'selectData') {
    // 获取列表当前选中数据内容
    const selectData = data?.selectData || ''
    if (selectData) {
      const selectDataIndexStr = source?.indexs[1] || ''
      let selectDataKey = selectDataIndexStr.split('.')
      selectDataKey = selectDataKey[selectDataKey.length - 1]
      return selectDataKey ? selectData[selectDataKey] : selectData
    } else return
  }

  // 如果数据源为空，返回null
  if (!data) {
    return null
  }

  const indices = indexStr.split('.')
  subIndexStr = subIndexStr + ''
  const subIndices = subIndexStr.split(',')
  let value = data
  try {
    for (let i = 0; i < indices.length; i++) {
      const index = indices[i]
      if (index.endsWith('[]')) {
        // 如果索引以 "[]" 结尾，那么我们需要获取数组的元素
        const arrayIndex = parseInt(subIndices.shift(), 10)
        value = value[index.slice(0, -2)][arrayIndex]
        if (value === undefined || value === null) {
          return null
        }
      } else if (containsArrayIndex(index)) {
        // 包含默认下标
        const result = extractAndRemoveArrayIndex(index)
        value = value[result.map][result.index]
        if (value === undefined || value === null) {
          return null
        }
      } else {
        // 检查对象属性是否存在
        if (typeof value !== 'object' || value === null || !(index in value)) {
          if (i === indices.length - 1) {
            // 兼容接口不返回对应空字段
            value = ''
          } else {
            value = null
          }
        } else {
          // 否则，我们直接获取对象的属性
          value = value[index]
        }
      }
    }
    return value
  } catch (err) {
    return null
  }
}

export const initDataSource = (uuid, dataSource, nodeData, hidenEl, listIndex, listItemData = null) => {
  if (!dataSource) return
  if (!useAppStore) return
  // 全局状态
  const appStore = useAppStore()
  const dataSourceDataKeys = Object.keys(dataSource)
  if (dataSourceDataKeys && dataSourceDataKeys.length) {
    dataSourceDataKeys.forEach((key) => {
      const config = dataSource[key]
      const shouldSkipDerived = createDerivedValueCache()
      let cb = null
      if (config.type === 'script') {
        if (listItemData != null && isAllUnifiedListDataSourceBindings(config.source, listIndex)) {
          applyListItemScriptDataSource(config, key, nodeData, hidenEl, listIndex, listItemData, shouldSkipDerived)
          return
        }
        // 脚本（注册时预编译，避免每次数据源更新重复 new Function）
        let compiledFunc = null
        try {
          compiledFunc = convertStringToFunc(config.script)
        } catch (error) {
          console.error('数据源脚本编译错误', uuid, error)
        }
        cb = () => {
          if (!compiledFunc) return
          const funcData = {}
          let isAllDataReady = true
          config.source.forEach((source, index) => {
            // 获取数据源
            let sourceUUID = source.uuid
            if (source.indexs === 'cpm-listIndex') {
              // 特殊处理，如果是列表组件中的组件，绑定，通过"cpm-listIndex"标识以来的数据源为动态的uuid
              sourceUUID = source.uuid + '_' + listIndex.index
            }
            const data = appStore.getPageDataSource(sourceUUID)
            // 判断是否为列表项内容,是则使用列表项索引
            const indexs = getDataSourceIndex(listIndex, source)
            const targetData = getDataSourceData(data, listIndex, source, uuid)
            const sd = getSourceMapData(targetData, source, indexs)
            funcData[index + ''] = sd
            if (sd === undefined || sd === null) {
              isAllDataReady = false
            }
          })
          if (isAllDataReady) {
            if (shouldSkipDerived(funcData)) return
            try {
              if (key === 'hiden') {
                const nextRaw = compiledFunc(...Object.values(funcData))
                setDataSourceHidenIfChanged(hidenEl, nextRaw)
              } else {
                const nextValue = compiledFunc(...Object.values(funcData))
                setNodeDataFieldIfChanged(nodeData, key, nextValue)
              }
            } catch (error) {
              console.error('数据源脚本执行错误', uuid, error)
            }
          }
        }
        // 判断是否为立即执行函数
        const hasFuncDS = config.source.find((item) => immediateFuncUUID.includes(item.uuid) || (item.uuid.includes('xy-list-item-layout') && item.keys === 'index'))
        if (hasFuncDS) {
          cb()
        }
      } else {
        // 填充
        const source = config.source[0]
        if (listItemData != null && isUnifiedListDataSourceBinding(source, listIndex)) {
          const value = getListItemFillValue(listItemData, source, listIndex)
          if (value !== undefined && value !== null) {
            if (key === 'hiden') {
              setDataSourceHidenIfChanged(hidenEl, value)
            } else if (!shouldSkipDerived(value)) {
              setNodeDataFieldIfChanged(nodeData, key, value)
            }
          }
          return
        }
        cb = () => {
          // 获取数据源
          let sourceUUID = source.uuid
          if (source.indexs === 'cpm-listIndex') {
            // 特殊处理，如果是列表组件中的组件，绑定，通过"cpm-listIndex"标识以来的数据源为动态的uuid
            sourceUUID = source.uuid + '_' + listIndex.index
          }
          const data = appStore.getPageDataSource(sourceUUID)
          // 判断是否为列表项内容,是则使用列表项索引
          const indexs = getDataSourceIndex(listIndex, source)
          const targetData = getDataSourceData(data, listIndex, source, uuid)
          const funcData = getSourceMapData(targetData, source, indexs)
          if (funcData !== undefined && funcData !== null) {
            if (key === 'hiden') {
              setDataSourceHidenIfChanged(hidenEl, funcData)
            } else {
              if (shouldSkipDerived(funcData)) return
              setNodeDataFieldIfChanged(nodeData, key, funcData)
            }
          }
        }
        // 判断是否为立即执行函数, 或者列表项内容
        if (immediateFuncUUID.includes(config.source[0].uuid) || (config.source[0].uuid.includes('xy-list-item-layout') && config.source[0].keys === 'index')) {
          cb()
        }
      }
      // 添加节点消费唯一cb
      config.source.forEach((source) => {
        if (listItemData != null && isUnifiedListDataSourceBinding(source, listIndex)) {
          return
        }
        if (listItemData != null && config.type === 'script' && isAllUnifiedListDataSourceBindings(config.source, listIndex)) {
          return
        }
        if (source.indexs === 'cpm-listIndex') {
          // 特殊处理，如果是列表组件中的组件，绑定，通过"cpm-listIndex"标识以来的数据源为动态的uuid
          appStore.setPageDataSourceCbMap(source.uuid + '_' + listIndex.index, `${uuid}-${key}`, cb, source.keys)
        } else if (!immediateFuncUUID.includes(source.uuid)) {
          appStore.setPageDataSourceCbMap(source.uuid, `${uuid}-${key}`, cb, source.keys)
        }
      })
    })
  }
}

/**
 * 获取数据源下标索引
 * @param {*} listIndex 列表子项信息
 * @param {*} sourceInfo 数据信息
 */
const getDataSourceIndex = (listIndex, sourceInfo) => {
  // 判断数据源id是否
  if (listIndex?.source?.uuid === sourceInfo.uuid) {
    return listIndex?.index ? listIndex.index : ''
  } else {
    return sourceInfo.indexs
  }
}

const getDataSourceData = (data, listIndex, sourceInfo, uuid) => {
  if (uuid && uuid.includes('xy-list-item-layout')) {
    // 判断获取数据的节点是不是列表子项节点, 是则只返回列表数据
    return data
  }
  if (listIndex?.source?.uuid === sourceInfo.uuid) {
    // 列表嵌套场景需注入 listData，仅此时深拷贝
    data = cloneDeep(data)
    // 获取真正的列表数据
    const appStore = useAppStore()
    // 获取列表数据源的下标索引
    const listDataKeys = listIndex.source.keys.split('.')
    const subIndices = listIndex.index.split(',')
    let listDS = appStore.getPageDataSource(listIndex.listUUID)
    if (subIndices.length > 1) {
      // 多级嵌套列表数据源, 根据下标索引获取列表数据
      const listSubIndices = subIndices.slice(0, subIndices.length - 1)
      const listIndexStr = listSubIndices.join(',')
      listDS = appStore.getPageDataSource(listIndex.listUUID + listIndexStr)
    }
    let current = data
    for (let i = 0; i < listDataKeys.length - 1; i++) {
      const index = listDataKeys[i]
      if (index.endsWith('[]')) {
        // 如果索引以 "[]" 结尾，那么我们需要获取数组的元素
        const arrayIndex = parseInt(subIndices.shift(), 10)
        current = current[index.slice(0, -2)][arrayIndex]
      } else {
        current = current[index]
      }
    }
    if (listDataKeys?.length && current && current[listDataKeys[listDataKeys.length - 1]]) {
      current[listDataKeys[listDataKeys.length - 1]] = listDS.listData || []
    }
    return data
  } else {
    return data
  }
}

/**
 * 列表子项选中数据保存
 * @param {*} listIndex
 */
export const saveListClickDataSource = (listIndex) => {
  if (listIndex?.source?.uuid) {
    const appStore = useAppStore()
    if (appStore) {
      // 获取当前列表项选中下标
      const indexAry = listIndex.index.split(',') || []
      // 获取数据源
      const ds = appStore.getPageDataSource(listIndex.source.uuid)
      const listDS = appStore.getPageDataSource(listIndex.listUUID)
      // 判断是否为列表项内容,是则使用列表项索引
      const indexs = getDataSourceIndex(listIndex, listIndex.source)
      const data = getDataSourceData(ds, listIndex, listIndex.source)
      const source = cloneDeep(listIndex.source)
      source.keys = listIndex.source.keys + '[]'
      const sd = getSourceMapData(data, source, indexs)
      const clickData = Object.assign(cloneDeep(listDS), {
        selectIndex: indexAry[indexAry.length - 1],
        selectData: sd || ''
      })
      console.log('当前选中列表项数据----', clickData)
      appStore.setPageDataSource(listIndex.listUUID, clickData)
    }
  }
}

/**
 * 清空列表项选中信息
 * @param {*} uuid
 */
export const clearListClickDataSource = (uuid) => {
  const appStore = useAppStore()
  if (appStore) {
    const clickData = {
      selectIndex: '',
      selectData: ''
    }
    appStore.setPageDataSource(uuid, clickData)
  }
}

/**
 * 获取函数数据
 * @param {*} config
 */
const getFuncData = (config) => {
  const appStore = useAppStore()
  if (config.uuid === import.meta.env.VITE_APP_H5_DATALANG || config.uuid === import.meta.env.VITE_APP_H5_DATALANG_OLD) {
    // 获取多语言配置
    if (appStore) {
      if (appStore.previewType === 'save' || appStore.previewType === 'formal') {
        const params = urlParam2Obj()
        return params.language || 'en'
      }
      return appStore.language || 'en'
    }
    return getLanguage()
  } else if (config.uuid === 'data-source_addClickCustomParams') {
    const customParams = appStore.getPageDataSource('data-source_addClickCustomParams')
    return customParams ? customParams[config.keys] : ''
  } else if (config.uuid === 'data-source_timeInfo') {
    // 获取服务器时间
    const timeInfo = appStore.getPageDataSource('data-source_timeInfo')
    if (config.keys.includes('current-time')) {
      // 找到最后一个 '-' 的位置
      const keysAry = config.keys.split('-')
      // 截取最后一个 '-' 后面的字符串
      const result = keysAry[keysAry.length - 1]
      // 如果获取不到服务器时间，获取设备时间
      let currentTime = new Date().getTime()
      if (timeInfo && timeInfo.serverTime && timeInfo.localTime) {
        const serverTime = timeInfo.serverTime
        const localTime = timeInfo.localTime
        const diffTime = serverTime - localTime
        // 计算最小服务器时间
        currentTime = new Date().getTime() + diffTime
      }
      // 时间判断函数
      return compareWithCurrentUTC(config.indexs, currentTime) === result
    } else {
      return timeInfo ? timeInfo[config.keys] : ''
    }
  }
  return null
}

/**
 * 数据源绑定
 * @param {*} data
 * @param {*} pageDataSource
 */
export const setParamsByDataSource = (data, pageDataSource) => {
  let isChecked = true
  Object.keys(data).forEach((key) => {
    // 判断是否绑定数据源
    if (isObject(data[key]) && data[key].source && data[key].source.length) {
      const dataSourceConfig = data[key]
      if (dataSourceConfig.type === 'string') {
        // 数据填充
        const dataSource = pageDataSource[dataSourceConfig.source[0].uuid]
        const func = getSourceMapData(dataSource, dataSourceConfig.source[0], dataSourceConfig.source[0].indexs)
        if (func !== undefined && func !== null) {
          data[key] = func
        } else {
          isChecked = false
        }
      } else if (dataSourceConfig.type === 'script') {
        // 数据脚本
        const funcData = {}
        let isAllDataReady = true
        dataSourceConfig.source.forEach((source, index) => {
          const dataSource = pageDataSource[source.uuid]
          const sd = getSourceMapData(dataSource, source, source.indexs)
          funcData[index + ''] = sd
          if (sd === undefined || sd === null) {
            isAllDataReady = false
          }
        })
        if (isAllDataReady) {
          try {
            const func = convertStringToFunc(dataSourceConfig.script)
            data[key] = func(...Object.values(funcData))
          } catch (error) {
            console.error('数据源脚本执行错误', error)
            isChecked = false
          }
        } else {
          isChecked = false
        }
      } else {
        isChecked = false
      }
    }
  })
  return isChecked ? data : null
}
